/**
 * Main Calculator class for RPN Calculator
 * Implements full RPN calculator logic with all operations
 *
 * All bug fixes from the plan are applied in this implementation
 */

import { Stack } from './Stack';
import { Storage } from './Storage';
import * as Ops from './Operations';
import { convertValue } from './ConversionData';
import {
  type TrigMode,
  type DisplayMode,
  type ArithmeticMode,
  type CommaType,
  type MemoryRegisters,
  type StatisticsItem,
  type ConversionConfig,
  type KeyStroke,
  KEY_TO_DIGIT
} from './types';

export type CommandResult = {
  display: string;
  command?: string;
  error?: boolean;
};

export class Calculator {
  // Stack
  private stack: Stack;

  // State
  private operationDone = true;
  private enterPressed = false;
  private displayBuffer = '0';
  private lastx = 0;

  // Modes
  private cmode: TrigMode = 'rad';
  private displaymode: DisplayMode = 'normal';
  private mode_f = false;
  private mode_g = false;
  private mode_hyp = false;

  // Display
  private displayprecision = 10;
  private displayStack = false;
  private commaType: CommaType = '3';

  // Storage operations
  private stopressed = false;
  private rclpressed = false;
  private stoarithmetic: ArithmeticMode = 'none';
  private rclarithmetic: ArithmeticMode = 'none';

  // Precision modes
  private fixpressed = false;
  private engpressed = false;
  private scipressed = false;

  // Memory
  private memoryregisters: MemoryRegisters;
  private statisticsregisters: StatisticsItem[] = [];
  private _conversionConfig: ConversionConfig;

  // Audio
  private audiofeedback = true;

  // UI state
  leaveStackInfo = false;

  // Callbacks for UI updates
  private onModeChange?: (mode: string, enabled: boolean) => void;

  constructor() {
    this.stack = new Stack();
    this.memoryregisters = {
      r0: 0, r1: 0, r2: 0, r3: 0, r4: 0,
      r5: 0, r6: 0, r7: 0, r8: 0, r9: 0
    };
    this._conversionConfig = {
      fromfactor: 1,
      tofactor: 1,
      property: 'Acceleration',
      propertyindex: 0,
      fromvalue: 1,
      tovalue: 1
    };

    this.initialize();
  }

  /**
   * Initialize calculator from storage
   */
  private initialize(): void {
    Storage.migrate();
    const data = Storage.load();

    if (data.stack) {
      this.stack.fromJSON(data.stack);
    }
    if (data.memoryregisters) {
      this.memoryregisters = data.memoryregisters;
    }
    if (data.statisticsregisters) {
      this.statisticsregisters = data.statisticsregisters;
    }
    if (data.displaystack !== undefined) {
      this.displayStack = data.displaystack;
    }
    if (data.displayprecision !== undefined) {
      this.displayprecision = data.displayprecision;
    }
    if (data.displaymode) {
      this.displaymode = data.displaymode;
    }
    if (data.cmode) {
      this.cmode = data.cmode;
    }
    if (data.audiofeedback !== undefined) {
      this.audiofeedback = data.audiofeedback;
    }
    if (data.commatype) {
      this.commaType = data.commatype;
    }
    if (data.conversionconfig) {
      this._conversionConfig = data.conversionconfig;
    }
  }

  /**
   * Set callback for mode changes
   */
  setModeChangeCallback(callback: (mode: string, enabled: boolean) => void): void {
    this.onModeChange = callback;
  }

  /**
   * Get current trig conversion factor
   */
  private get conversion(): number {
    return Ops.getTrigConversion(this.cmode);
  }

  /**
   * Save stack to storage
   */
  private saveStack(): void {
    Storage.saveStack(this.stack.toJSON());
  }

  /**
   * Format display buffer for output
   */
  getDisplayBuffer(done = false): string {
    let data = this.displayBuffer.toString();

    // FIX #4: Use Number.isNaN() instead of === NaN
    if (data === 'NaN' || data === 'Infinity' || data === 'undefined' ||
        Number.isNaN(parseFloat(data)) && data !== '') {
      this.stack.undo();
      return 'ERROR';
    }

    data = data.replace(/^\./, '').replace(/\.$/, '');

    if (this.operationDone || done) {
      if (data === '') return '';

      switch (this.displaymode) {
        case 'fix':
          data = parseFloat(data).toFixed(this.displayprecision);
          break;
        case 'sci':
          data = this.formatSCI(data, this.displayprecision);
          break;
        case 'eng':
          data = this.formatENG(data, this.displayprecision);
          break;
      }
    }

    if (data.length > 21) {
      data = parseFloat(data).toExponential(this.displayprecision);
    }

    return data;
  }

  /**
   * Format number in scientific notation
   */
  private formatSCI(number: string, fix: number): string {
    const num = parseFloat(number);
    if (!Number.isFinite(num)) return number;

    const exp = num.toExponential(fix);
    return exp.includes('e') ? exp : exp + 'e0';
  }

  /**
   * Format number in engineering notation (exponent multiple of 3)
   */
  private formatENG(number: string, fix: number): string {
    const num = parseFloat(number);
    if (!Number.isFinite(num) || num === 0) {
      return num === 0 ? '0' : number;
    }

    const exp = Math.floor(Math.log10(Math.abs(num)));
    const engExp = Math.floor(exp / 3) * 3;
    const mantissa = num / Math.pow(10, engExp);

    // Determine decimal places
    const mantissaStr = mantissa.toString();
    const dotPos = mantissaStr.indexOf('.');
    const digitsBeforeDot = dotPos === -1 ? mantissaStr.length : dotPos;
    const decimalPlaces = Math.max(0, fix - digitsBeforeDot + 1);

    const formatted = mantissa.toFixed(decimalPlaces);
    return engExp === 0 ? formatted : `${formatted}e${engExp}`;
  }

  /**
   * Reset f/g modes
   */
  resetModes(): void {
    this.mode_f = false;
    this.mode_g = false;
    this.onModeChange?.('f', false);
    this.onModeChange?.('g', false);
  }

  /**
   * Handle storage operations (STO/RCL)
   */
  private handleStorage(type: KeyStroke): string | null {
    const digit = KEY_TO_DIGIT[type];

    if (this.stopressed) {
      if (this.stoarithmetic !== 'none' && typeof digit === 'number') {
        const regKey = `r${digit}` as keyof MemoryRegisters;
        let newVal: number;

        switch (this.stoarithmetic) {
          case 'plus':
            newVal = this.memoryregisters[regKey] + this.stack.x;
            break;
          case 'minus':
            newVal = this.memoryregisters[regKey] - this.stack.x;
            break;
          case 'divide':
            // FIX #1: Safe division
            newVal = Ops.smartRound(Ops.safeDivide(this.memoryregisters[regKey], this.stack.x));
            break;
          case 'multiply':
            newVal = Ops.smartRound(this.memoryregisters[regKey] * this.stack.x);
            break;
          default:
            newVal = NaN;
        }

        this.stopressed = false;
        this.stoarithmetic = 'none';
        this.onModeChange?.('sto', false);

        // FIX #4: Proper NaN check
        if (Number.isNaN(newVal)) {
          return 'ERROR';
        }

        this.memoryregisters[regKey] = newVal;
        Storage.saveMemoryRegisters(this.memoryregisters);
        return this.getDisplayBuffer();
      }

      if (type !== 'dot' && typeof digit === 'number') {
        const regKey = `r${digit}` as keyof MemoryRegisters;
        this.memoryregisters[regKey] = this.stack.x;
        Storage.saveMemoryRegisters(this.memoryregisters);
        this.stopressed = false;
        this.onModeChange?.('sto', false);
        this.operationDone = true;
      }

      if (['minus', 'plus', 'divide', 'multiply'].includes(type)) {
        this.stoarithmetic = type as ArithmeticMode;
        return this.getDisplayBuffer();
      } else {
        this.stoarithmetic = 'none';
      }

      this.stopressed = false;
      return this.getDisplayBuffer();
    }

    if (this.rclpressed) {
      if (this.rclarithmetic !== 'none' && typeof digit === 'number') {
        const regKey = `r${digit}` as keyof MemoryRegisters;
        let result: number;

        switch (this.rclarithmetic) {
          case 'plus':
            result = this.stack.x + this.memoryregisters[regKey];
            break;
          case 'minus':
            result = this.stack.x - this.memoryregisters[regKey];
            break;
          case 'divide':
            // FIX #1: Safe division
            result = Ops.smartRound(Ops.safeDivide(this.stack.x, this.memoryregisters[regKey]));
            break;
          case 'multiply':
            result = Ops.smartRound(this.stack.x * this.memoryregisters[regKey]);
            break;
          default:
            result = NaN;
        }

        this.rclpressed = false;
        this.rclarithmetic = 'none';

        // FIX #4: Proper NaN check
        if (Number.isNaN(result)) {
          return 'ERROR';
        }

        this.lastx = this.stack.x;
        this.stack.x = result;
        this.displayBuffer = result.toString();
        this.operationDone = true;
        return this.getDisplayBuffer();
      }

      if (['minus', 'plus', 'divide', 'multiply'].includes(type)) {
        this.rclarithmetic = type as ArithmeticMode;
        return this.getDisplayBuffer();
      } else {
        this.rclarithmetic = 'none';
      }

      if (type !== 'dot' && typeof digit === 'number') {
        const regKey = `r${digit}` as keyof MemoryRegisters;
        this.stack.pushX();
        this.stack.x = this.memoryregisters[regKey];
        this.displayBuffer = this.stack.x.toString();
        this.operationDone = true;
      }

      this.rclpressed = false;
      return this.getDisplayBuffer();
    }

    return null; // Not a storage operation
  }

  /**
   * Handle FIX/SCI/ENG mode setting
   */
  private handlePrecisionMode(type: KeyStroke): string | null {
    const digit = KEY_TO_DIGIT[type];

    if (this.fixpressed) {
      return this.handlePrecisionSet('fix', type, digit);
    }
    if (this.scipressed) {
      return this.handlePrecisionSet('sci', type, digit);
    }
    if (this.engpressed) {
      return this.handlePrecisionSet('eng', type, digit);
    }

    return null;
  }

  private handlePrecisionSet(mode: 'fix' | 'sci' | 'eng', type: KeyStroke, digit: number | string | undefined): string {
    if (digit === undefined) {
      this[`${mode}pressed` as 'fixpressed' | 'scipressed' | 'engpressed'] = false;
      if (this.displaymode !== mode) {
        this.onModeChange?.(mode, false);
      }
      return this.getDisplayBuffer();
    }

    if (type !== 'dot' && typeof digit === 'number') {
      this.displayprecision = digit;
    }

    if (type === 'dot') {
      this.onModeChange?.(mode, false);

      if (this.displaymode !== mode) {
        this[`${mode}pressed` as 'fixpressed' | 'scipressed' | 'engpressed'] = false;
        return this.getDisplayBuffer(true);
      }

      this.displayprecision = 10;
      this.displaymode = 'normal';
      Storage.saveDisplayMode(this.displaymode);
      this[`${mode}pressed` as 'fixpressed' | 'scipressed' | 'engpressed'] = false;
      this.operationDone = true;
      return this.getDisplayBuffer(true);
    }

    Storage.saveDisplayPrecision(this.displayprecision);
    this.displaymode = mode;
    Storage.saveDisplayMode(this.displaymode);
    this[`${mode}pressed` as 'fixpressed' | 'scipressed' | 'engpressed'] = false;

    // Turn off other modes
    ['fix', 'sci', 'eng'].forEach(m => {
      this.onModeChange?.(m, m === mode);
    });

    this.operationDone = true;
    return this.getDisplayBuffer(true);
  }

  /**
   * Handle number keys with secondary functions
   */
  private handleNumbersWithFunctions(type: KeyStroke): string | null {
    const digit = KEY_TO_DIGIT[type];
    if (digit === undefined) return null;

    // G-shifted functions for number keys
    if (this.mode_g) {
      switch (type) {
        case 'seven': // DEG mode
          this.cmode = 'deg';
          Storage.saveTrigMode(this.cmode);
          this.resetModes();
          this.onModeChange?.('deg', true);
          this.onModeChange?.('rad', false);
          this.onModeChange?.('grad', false);
          return this.getDisplayBuffer();

        case 'eight': // RAD mode
          this.cmode = 'rad';
          Storage.saveTrigMode(this.cmode);
          this.resetModes();
          this.onModeChange?.('rad', true);
          this.onModeChange?.('deg', false);
          this.onModeChange?.('grad', false);
          return this.getDisplayBuffer();

        case 'nine': // GRAD mode
          this.cmode = 'grad';
          Storage.saveTrigMode(this.cmode);
          this.resetModes();
          this.onModeChange?.('grad', true);
          this.onModeChange?.('deg', false);
          this.onModeChange?.('rad', false);
          return this.getDisplayBuffer();

        case 'four': // FIX
          this.resetModes();
          this.onModeChange?.('fix', true);
          this.fixpressed = true;
          return this.getDisplayBuffer();

        case 'five': // SCI
          this.resetModes();
          this.onModeChange?.('sci', true);
          this.scipressed = true;
          return this.getDisplayBuffer();

        case 'six': // ENG
          this.resetModes();
          this.onModeChange?.('eng', true);
          this.engpressed = true;
          return this.getDisplayBuffer();

        case 'zero': // to H.MS
          this.lastx = this.stack.x;
          this.stack.x = Ops.toHMS(this.stack.x);
          this.displayBuffer = this.stack.x.toString();
          this.operationDone = true;
          this.resetModes();
          return this.displayBuffer;

        case 'dot': // to H
          this.lastx = this.stack.x;
          this.stack.x = Ops.fromHMS(this.stack.x);
          this.displayBuffer = this.stack.x.toString();
          this.operationDone = true;
          this.resetModes();
          return this.displayBuffer;

        case 'one': // to HEX
          this.operationDone = true;
          this.resetModes();
          return Math.floor(this.stack.x).toString(16).toUpperCase();

        case 'two': // to BIN
          this.operationDone = true;
          this.resetModes();
          return Math.floor(this.stack.x).toString(2);

        case 'three': // to OCT
          this.operationDone = true;
          this.resetModes();
          return Math.floor(this.stack.x).toString(8);
      }
    }

    // F-shifted functions for number keys
    if (this.mode_f) {
      switch (type) {
        case 'seven': // Permutation P(y,x)
          try {
            this.stack.dropOne(Ops.permutation(this.stack.y, this.stack.x));
          } catch {
            this.resetModes();
            return 'ERROR';
          }
          this.operationDone = true;
          this.resetModes();
          this.displayBuffer = this.stack.x.toString();
          return this.getDisplayBuffer();

        case 'eight': // Combination C(y,x)
          try {
            this.stack.dropOne(Ops.combination(this.stack.y, this.stack.x));
          } catch {
            this.resetModes();
            return 'ERROR';
          }
          this.operationDone = true;
          this.resetModes();
          this.displayBuffer = this.stack.x.toString();
          return this.getDisplayBuffer();

        case 'nine': // Linear regression L.R.
          Ops.recalculateStatistics(this.memoryregisters, this.statisticsregisters);
          const { slope, intercept } = Ops.calculateLinearRegression(this.memoryregisters);
          this.stack.pushX();
          this.stack.pushX();
          this.stack.y = slope;
          this.stack.x = intercept;
          this.displayBuffer = this.stack.x.toString();
          this.operationDone = true;
          this.resetModes();
          return this.getDisplayBuffer();
      }
    }

    return null;
  }

  /**
   * Handle dot key
   */
  private handleDot(): string | null {
    if (this.enterPressed) {
      this.enterPressed = false;
      this.displayBuffer = '0.';
      return this.displayBuffer;
    }

    if (this.operationDone) {
      this.operationDone = false;
      this.stack.pushX();
      this.displayBuffer = '0.';
      this.stack.x = 0;
      return this.displayBuffer;
    }

    if (this.displayBuffer.toString().length !== 0) {
      if (!this.displayBuffer.toString().includes('.')) {
        this.displayBuffer = this.displayBuffer.toString() + '.';
      }
      return this.displayBuffer;
    } else {
      this.displayBuffer = '0.';
      return this.displayBuffer;
    }
  }

  /**
   * Handle digit input after operation or enter
   */
  private handleDoneAndEnter(type: KeyStroke): string | null {
    const digit = KEY_TO_DIGIT[type];
    if (digit === undefined) return null;

    if (this.operationDone) {
      this.stack.pushX();
      this.operationDone = false;
      this.stack.x = typeof digit === 'number' ? digit : 0;
      this.displayBuffer = String(digit);
      return this.getDisplayBuffer();
    }

    if (this.enterPressed) {
      this.enterPressed = false;
      this.stack.x = typeof digit === 'number' ? digit : 0;
      this.displayBuffer = String(digit);
      return this.getDisplayBuffer();
    }

    return null;
  }

  /**
   * Main command handler
   */
  doCommand(type: KeyStroke): CommandResult {
    if (type === 'refresh') {
      return { display: this.getDisplayBuffer() };
    }

    // Protection against stack corruption
    if (this.stack.x === null || this.stack.x === undefined) {
      this.stack = new Stack();
    }

    const digit = KEY_TO_DIGIT[type];

    // Storage operations
    if (digit !== undefined || ['divide', 'plus', 'minus', 'multiply'].includes(type)) {
      const storageResult = this.handleStorage(type);
      if (storageResult !== null) {
        this.saveStack();
        return { display: storageResult };
      }
    }

    // Numeric input
    if (digit !== undefined) {
      // Precision mode handling
      const precisionResult = this.handlePrecisionMode(type);
      if (precisionResult !== null) {
        this.saveStack();
        return { display: precisionResult };
      }

      // Numbers with secondary functions
      const numbersResult = this.handleNumbersWithFunctions(type);
      if (numbersResult !== null) {
        this.saveStack();
        return { display: numbersResult };
      }

      // Dot handling
      if (type === 'dot') {
        const dotResult = this.handleDot();
        if (dotResult !== null) {
          return { display: dotResult };
        }
      }

      // Done and enter handling
      const doneResult = this.handleDoneAndEnter(type);
      if (doneResult !== null) {
        this.saveStack();
        return { display: doneResult };
      }

      // Limit input to 14 digits
      if (this.getDisplayBuffer().length > 14) {
        return { display: this.getDisplayBuffer() };
      }

      // Prevent leading zeros
      if (this.displayBuffer === '0' && type === 'zero') {
        return { display: this.getDisplayBuffer() };
      }

      // Replace leading zero
      if (this.displayBuffer === '0' && type !== 'dot') {
        this.displayBuffer = String(digit);
        return { display: this.getDisplayBuffer() };
      }

      // Append digit
      this.displayBuffer = this.displayBuffer.toString() + digit;
      this.stack.x = parseFloat(this.getDisplayBuffer()) || 0;
      return { display: this.getDisplayBuffer() };
    }

    // Handle precision mode errors
    if (this.engpressed || this.scipressed || this.fixpressed) {
      this.engpressed = false;
      this.scipressed = false;
      this.fixpressed = false;
      return { display: 'ERROR', error: true };
    }

    // Handle non-numeric commands
    const result = this.handleCommand(type);
    this.saveStack();
    return result;
  }

  /**
   * Handle non-numeric commands
   */
  private handleCommand(type: KeyStroke): CommandResult {
    switch (type) {
      case 'sigmaplus':
        return this.handleSigmaPlus();

      case 'sto':
        return this.handleSto();

      case 'rcl':
        return this.handleRcl();

      case 'f':
        return this.handleFMode();

      case 'g':
        return this.handleGMode();

      case 'enter':
        return this.handleEnter();

      case 'plus':
        return this.handlePlus();

      case 'minus':
        return this.handleMinus();

      case 'multiply':
        return this.handleMultiply();

      case 'divide':
        return this.handleDivide();

      case 'undo':
        return this.handleUndo();

      case 'ex':
        return this.handleEx();

      case 'tenx':
        return this.handleTenX();

      case 'sin':
        return this.handleSin();

      case 'cos':
        return this.handleCos();

      case 'tan':
        return this.handleTan();

      case 'pi':
        return this.handlePi();

      case 'xsquare':
        return this.handleXSquare();

      case 'onedivx':
        return this.handleOneDivX();

      case 'xfac':
        return this.handleXFac();

      case 'sqrt':
        return this.handleSqrt();

      case 'percent':
        return this.handlePercent();

      case 'swapxy':
        return this.handleSwapXY();

      case 'roll':
        return this.handleRoll();

      case 'chs':
        return this.handleChs();

      case 'backspace':
        return this.handleBackspace();

      case 'eex':
        return this.handleEex();

      case 'conv':
        return this.handleConv();

      default:
        return { display: this.getDisplayBuffer() };
    }
  }

  // Individual command handlers

  private handleSigmaPlus(): CommandResult {
    if (this.mode_f) {
      // Clear statistics
      this.statisticsregisters = [];
      for (let i = 2; i < 8; i++) {
        this.memoryregisters[`r${i}` as keyof MemoryRegisters] = 0;
      }
      Storage.saveMemoryRegisters(this.memoryregisters);
      Storage.saveStatisticsRegisters(this.statisticsregisters);
      this.stack.clear();
      this.resetModes();
      this.displayBuffer = '';
      return { display: this.getDisplayBuffer() };
    }

    if (this.mode_g) {
      // Remove stat item
      this.statisticsregisters = Ops.removeStatisticsItem(
        this.memoryregisters,
        this.statisticsregisters,
        this.stack.x,
        this.stack.y
      );
    } else {
      // Add stat item
      this.statisticsregisters = Ops.addStatisticsItem(
        this.memoryregisters,
        this.statisticsregisters,
        this.stack.x,
        this.stack.y
      );
    }

    Storage.saveStatisticsRegisters(this.statisticsregisters);
    Storage.saveMemoryRegisters(this.memoryregisters);
    this.stack.x = this.statisticsregisters.length;
    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    this.resetModes();
    return { display: this.getDisplayBuffer() };
  }

  private handleSto(): CommandResult {
    if (this.mode_g) {
      // Mean x-bar
      const { meanX, meanY } = Ops.calculateMean(this.memoryregisters);
      this.stack.pushX();
      this.stack.pushX();
      this.stack.x = meanX;
      this.stack.y = meanY;
      this.operationDone = true;
      this.resetModes();
      this.displayBuffer = this.stack.x.toString();
      return { display: this.getDisplayBuffer() };
    }

    this.stopressed = true;
    this.onModeChange?.('sto', true);
    return { display: this.getDisplayBuffer() };
  }

  private handleRcl(): CommandResult {
    if (this.mode_g) {
      // Clear stack
      this.resetModes();
      this.stack.clear();
      this.displayBuffer = '';
      this.operationDone = true;
      return { display: this.getDisplayBuffer() };
    }

    this.rclpressed = true;
    return { display: this.getDisplayBuffer() };
  }

  private handleFMode(): CommandResult {
    if (this.mode_f) {
      this.onModeChange?.('f', false);
      this.mode_f = false;
    } else {
      this.resetModes();
      this.onModeChange?.('f', true);
      this.mode_f = true;
    }
    return { display: this.getDisplayBuffer() };
  }

  private handleGMode(): CommandResult {
    if (this.mode_g) {
      this.onModeChange?.('g', false);
      this.mode_g = false;
    } else {
      this.resetModes();
      this.onModeChange?.('g', true);
      this.mode_g = true;
    }
    return { display: this.getDisplayBuffer() };
  }

  private handleEnter(): CommandResult {
    if (this.mode_f) {
      // Random number
      this.stack.pushX();
      this.stack.x = Math.random();
      this.resetModes();
      this.operationDone = true;
      this.displayBuffer = this.stack.x.toString();
    } else if (this.mode_g) {
      // LASTx
      this.stack.pushX();
      this.stack.x = this.lastx;
      this.resetModes();
      this.displayBuffer = this.stack.x.toString();
    } else {
      this.stack.pushX();
      if (this.operationDone) {
        this.operationDone = false;
      }
      this.enterPressed = true;
      return { display: this.getDisplayBuffer(true) };
    }
    return { display: this.getDisplayBuffer() };
  }

  private handlePlus(): CommandResult {
    if (this.mode_g) {
      // RND - round to display precision
      this.resetModes();
      this.operationDone = true;
      return { display: this.getDisplayBuffer() };
    }

    this.lastx = this.stack.x;
    this.stack.dropOne(Ops.smartRound(this.stack.x + this.stack.y));
    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handleMinus(): CommandResult {
    this.lastx = this.stack.x;

    if (this.mode_g) {
      // INT
      this.resetModes();
      this.stack.x = Ops.intPart(this.stack.x);
      this.displayBuffer = this.stack.x.toString();
      this.operationDone = true;
      return { display: this.getDisplayBuffer() };
    }

    if (this.mode_f) {
      // FRAC - FIX #6 applied in Operations.ts
      this.resetModes();
      this.stack.x = Ops.fracPart(this.stack.x);
      this.displayBuffer = this.stack.x.toString();
      this.operationDone = true;
      return { display: this.getDisplayBuffer() };
    }

    this.stack.dropOne(Ops.smartRound(this.stack.y - this.stack.x));
    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handleMultiply(): CommandResult {
    this.lastx = this.stack.x;

    if (this.mode_g) {
      // Radians to degrees
      this.resetModes();
      this.stack.x = Ops.smartRound(this.stack.x * 180 / Math.PI);
      this.displayBuffer = this.stack.x.toString();
      this.operationDone = true;
      return { display: this.getDisplayBuffer() };
    }

    if (this.mode_f) {
      // Polar to rectangular
      const { x, y } = Ops.polarToRect(this.stack.x, this.stack.y, this.conversion);
      this.stack.x = x;
      this.stack.y = y;
      this.displayBuffer = this.stack.x.toString();
      this.operationDone = true;
      this.resetModes();
      return { display: this.getDisplayBuffer() };
    }

    this.stack.dropOne(Ops.smartRound(this.stack.y * this.stack.x));
    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handleDivide(): CommandResult {
    this.lastx = this.stack.x;

    if (this.mode_g) {
      // Degrees to radians
      this.resetModes();
      this.stack.x = Ops.smartRound(this.stack.x * Math.PI / 180);
      this.displayBuffer = this.stack.x.toString();
      this.operationDone = true;
      return { display: this.getDisplayBuffer() };
    }

    if (this.mode_f) {
      // Rectangular to polar
      const { r, theta } = Ops.rectToPolar(this.stack.x, this.stack.y, this.conversion);
      this.stack.x = r;
      this.stack.y = theta;
      this.displayBuffer = this.stack.x.toString();
      this.resetModes();
      this.operationDone = true;
      return { display: this.getDisplayBuffer() };
    }

    // FIX #1: Safe division
    this.stack.dropOne(Ops.safeDivide(this.stack.y, this.stack.x));
    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handleUndo(): CommandResult {
    if (this.mode_g) {
      this.resetModes();
      return { display: this.getDisplayBuffer(), command: 'STACK_INSPECTOR' };
    }

    this.stack.undo();
    this.operationDone = true;
    this.displayBuffer = this.stack.x.toString();
    return { display: this.getDisplayBuffer() };
  }

  private handleEx(): CommandResult {
    this.lastx = this.stack.x;

    if (this.mode_g) {
      // LN
      this.stack.x = Ops.smartRound(Math.log(this.stack.x));
      this.operationDone = true;
    } else {
      // e^x
      this.stack.x = Ops.smartRound(Math.pow(Math.E, this.stack.x));
    }

    this.resetModes();
    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handleTenX(): CommandResult {
    this.lastx = this.stack.x;

    if (this.mode_g) {
      // LOG
      this.stack.x = Ops.smartRound(Math.log(this.stack.x) / Math.LN10);
    } else {
      // 10^x
      this.stack.x = Ops.smartRound(Math.pow(10, this.stack.x));
    }

    this.resetModes();
    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handleSin(): CommandResult {
    this.lastx = this.stack.x;

    if (this.mode_g) {
      if (this.mode_hyp) {
        // asinh
        this.stack.x = Ops.asinh(this.stack.x);
      } else {
        // asin
        this.stack.x = Math.asin(this.stack.x) / this.conversion;
      }
    } else {
      if (this.mode_f) {
        // Toggle HYP mode
        this.mode_hyp = !this.mode_hyp;
        this.onModeChange?.('hyp', this.mode_hyp);
        this.resetModes();
        return { display: this.getDisplayBuffer() };
      }

      if (this.mode_hyp) {
        // sinh
        this.stack.x = Ops.sinh(this.stack.x);
      } else {
        // sin
        const val = this.stack.x * this.conversion;
        if (this.cmode === 'rad' && val !== 0 && val / Math.PI === Math.floor(val / Math.PI)) {
          this.stack.x = 0;
        } else {
          this.stack.x = Math.sin(val);
        }
      }
    }

    this.resetModes();
    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handleCos(): CommandResult {
    this.lastx = this.stack.x;

    if (this.mode_g) {
      if (this.mode_hyp) {
        // acosh - FIX #8 applied in Operations.ts
        this.stack.x = Ops.acosh(this.stack.x);
      } else {
        // acos
        this.stack.x = Math.acos(this.stack.x) / this.conversion;
      }
    } else {
      if (this.mode_hyp) {
        // cosh
        this.stack.x = Ops.cosh(this.stack.x);
      } else {
        // cos
        const val = this.stack.x * this.conversion;
        if (this.cmode === 'rad' && val !== 0 && val / Math.PI === Math.floor(val / Math.PI)) {
          this.stack.x = -1;
        } else {
          this.stack.x = Math.cos(val);
        }
      }
    }

    this.resetModes();
    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handleTan(): CommandResult {
    this.lastx = this.stack.x;

    if (this.mode_g) {
      if (this.mode_hyp) {
        // atanh
        this.stack.x = Ops.atanh(this.stack.x);
      } else {
        // atan
        this.stack.x = Math.atan(this.stack.x) / this.conversion;
      }
    } else {
      if (this.mode_hyp) {
        // tanh
        this.stack.x = Ops.tanh(this.stack.x);
      } else {
        // tan
        this.stack.x = Math.tan(this.stack.x * this.conversion);
      }
    }

    this.resetModes();
    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handlePi(): CommandResult {
    if (this.mode_g) {
      // e
      this.stack.pushX();
      this.stack.x = Math.E;
      this.resetModes();
    } else if (this.mode_f) {
      // phi (golden ratio)
      this.stack.pushX();
      this.stack.x = 1.618033988749895;
      this.resetModes();
    } else {
      // pi
      this.stack.pushX();
      this.stack.x = Math.PI;
    }

    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handleXSquare(): CommandResult {
    this.lastx = this.stack.x;

    if (this.mode_g) {
      // x^3
      this.stack.x = Ops.smartRound(Math.pow(this.stack.x, 3));
      this.resetModes();
    } else {
      // x^2
      this.stack.x = Ops.smartRound(Math.pow(this.stack.x, 2));
    }

    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handleOneDivX(): CommandResult {
    this.lastx = this.stack.x;

    if (this.mode_g) {
      // y^x
      this.stack.dropOne(Math.pow(this.stack.y, this.stack.x));
      this.displayBuffer = this.stack.x.toString();
      this.operationDone = true;
      this.resetModes();
    } else {
      // 1/x - FIX #1: handled by safe division in display
      this.stack.x = Ops.safeDivide(1, this.stack.x);
      this.displayBuffer = this.stack.x.toString();
      this.operationDone = true;
    }

    return { display: this.getDisplayBuffer() };
  }

  private handleXFac(): CommandResult {
    if (this.mode_f) {
      // 42 (the answer)
      this.stack.pushX();
      this.stack.x = 42;
      this.resetModes();
      this.operationDone = true;
      this.displayBuffer = this.stack.x.toString();
      return { display: this.getDisplayBuffer() };
    }

    this.lastx = this.stack.x;

    // FIX #2: Factorial with validation
    try {
      this.stack.x = Ops.factorial(this.stack.x);
    } catch {
      this.resetModes();
      return { display: 'ERROR', error: true };
    }

    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handleSqrt(): CommandResult {
    this.lastx = this.stack.x;

    if (this.mode_g) {
      // Cube root
      this.stack.x = Ops.smartRound(Math.pow(this.stack.x, 1 / 3));
      this.resetModes();
    } else {
      // Square root
      this.stack.x = Ops.smartRound(Math.sqrt(this.stack.x));
    }

    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handlePercent(): CommandResult {
    const xVal = this.stack.x;
    const yVal = this.stack.y;
    this.lastx = this.stack.x;

    if (this.mode_g) {
      // Delta %
      this.stack.x = Ops.smartRound(((xVal - yVal) / yVal) * 100);
      this.resetModes();
    } else {
      // %
      this.stack.x = Ops.smartRound((yVal / 100) * xVal);
    }

    this.displayBuffer = this.stack.x.toString();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handleSwapXY(): CommandResult {
    if (this.mode_g) {
      // Standard deviation
      const { sdX, sdY } = Ops.calculateStdDev(this.memoryregisters);
      this.stack.pushX();
      this.stack.pushX();
      this.stack.x = sdX;
      this.stack.y = sdY;
      this.displayBuffer = sdX.toString();
      this.resetModes();
      this.operationDone = true;
      return { display: this.getDisplayBuffer() };
    }

    // FIX #5: swapXY now properly copies in Stack.ts
    this.stack.swapXY();
    this.displayBuffer = this.stack.x.toString();
    this.resetModes();
    this.operationDone = true;
    return { display: this.getDisplayBuffer() };
  }

  private handleRoll(): CommandResult {
    if (this.mode_g) {
      this.stack.rollUp();
      this.resetModes();
    } else {
      this.stack.rollDown();
    }

    this.operationDone = true;
    this.displayBuffer = this.stack.x.toString();
    return { display: this.getDisplayBuffer() };
  }

  private handleChs(): CommandResult {
    if (this.mode_g) {
      // ABS
      this.stack.x = Math.abs(this.stack.x);
      this.resetModes();
      this.displayBuffer = this.stack.x.toString();
    } else {
      // Change sign
      if (this.enterPressed) {
        this.stack.x = -this.stack.x;
        this.stack.pushX();
        this.stack.x = -this.stack.x;
      }
      this.stack.x = -this.stack.x;
      this.displayBuffer = this.stack.x.toString();
    }
    return { display: this.displayBuffer };
  }

  private handleBackspace(): CommandResult {
    if (this.mode_g) {
      // CLx
      this.operationDone = false;
      this.stack.x = 0;
      this.displayBuffer = '';
      this.resetModes();
      return { display: this.getDisplayBuffer() };
    }

    if (this.mode_f) {
      // y,r (predicted Y and correlation)
      Ops.recalculateStatistics(this.memoryregisters, this.statisticsregisters);
      const { slope, intercept } = Ops.calculateLinearRegression(this.memoryregisters);
      const r = Ops.calculateCorrelation(this.memoryregisters);
      const predictedY = slope * this.stack.x + intercept;
      this.stack.pushX();
      this.stack.pushX();
      this.stack.x = predictedY;
      this.stack.y = r;
      this.displayBuffer = predictedY.toString();
      this.resetModes();
      this.operationDone = true;
      return { display: this.getDisplayBuffer() };
    }

    if (this.enterPressed) {
      this.displayBuffer = '';
      this.stack.x = 0;
      this.enterPressed = false;
      return { display: this.displayBuffer };
    }

    if (this.operationDone) {
      this.displayBuffer = '';
      this.stack.x = 0;
      this.operationDone = false;
      return { display: this.displayBuffer };
    }

    if (this.displayBuffer.toString().length === 1) {
      this.stack.x = 0;
      this.displayBuffer = '';
      return { display: this.displayBuffer };
    }

    if (this.displayBuffer.toString().length > 0) {
      this.displayBuffer = this.displayBuffer.toString().slice(0, -1);
      if (this.displayBuffer === '-') {
        this.displayBuffer = '';
        this.stack.x = 0;
        return { display: this.displayBuffer };
      }
      this.stack.x = parseFloat(this.displayBuffer) || 0;
      return { display: this.displayBuffer };
    }

    return { display: this.displayBuffer };
  }

  private handleEex(): CommandResult {
    const buf = this.displayBuffer.toString();

    if (buf.includes('e') || buf === '0' || buf === '' || this.operationDone) {
      return { display: this.displayBuffer };
    }

    if (this.mode_g) {
      this.resetModes();
      this.displayBuffer = buf + 'e-';
    } else {
      this.displayBuffer = buf + 'e';
    }

    return { display: this.displayBuffer };
  }

  private handleConv(): CommandResult {
    this.lastx = this.stack.x;

    const result = convertValue(
      this.stack.x,
      this._conversionConfig.propertyindex,
      this._conversionConfig.fromvalue,
      this._conversionConfig.tovalue
    );

    this.stack.x = Ops.smartRound(result);
    this.displayBuffer = this.stack.x.toString();
    this.leaveStackInfo = true;
    this.operationDone = true;
    return { display: this.getDisplayBuffer(), command: 'CONVERSION' };
  }

  // Getters for UI
  get currentDisplayMode(): DisplayMode {
    return this.displaymode;
  }

  get currentTrigMode(): TrigMode {
    return this.cmode;
  }

  get currentCommaType(): CommaType {
    return this.commaType;
  }

  get isDisplayStackEnabled(): boolean {
    return this.displayStack;
  }

  get isAudioFeedbackEnabled(): boolean {
    return this.audiofeedback;
  }

  get stackCards(): number[] {
    return this.stack.getCards();
  }

  get isModeF(): boolean {
    return this.mode_f;
  }

  get isModeG(): boolean {
    return this.mode_g;
  }

  get isModeHyp(): boolean {
    return this.mode_hyp;
  }

  get conversionConfig(): ConversionConfig {
    return this._conversionConfig;
  }

  // Setters for preferences
  setDisplayStack(enabled: boolean): void {
    this.displayStack = enabled;
    Storage.saveDisplayStack(enabled);
  }

  setAudioFeedback(enabled: boolean): void {
    this.audiofeedback = enabled;
    Storage.saveAudioFeedback(enabled);
  }

  setCommaType(type: CommaType): void {
    this.commaType = type;
    Storage.saveCommaType(type);
  }

  setConversionConfig(config: ConversionConfig): void {
    this._conversionConfig = config;
    Storage.saveConversionConfig(config);
  }
}
